import { useState, type ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { UsageBar } from 'features/usage/components/usage-bar';
import {
  formatNumber,
  formatPercent,
  formatResetTime,
  formatShortDate,
  formatWindowName,
} from 'features/usage/utils/usage-format';
import { cn } from 'lib/utils';
import { useTopUpCoversUsage } from '../hooks/use-credits';
import {
  useRefreshUsageAfterReply,
  useUsageLimitIndicator,
} from '../hooks/use-usage-limit-indicator';

const RADIUS = 7;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const CARD_CLASS =
  'rounded-field border-hairline border-grey-300 bg-true-white text-popover-foreground shadow-popover-light dark:border-border dark:bg-popover';

/** Used share of a limit as a ring. Green, amber from 80%, red when full; calm when top-up credits cover it. */
const UsageRing = ({ percent = 0, calm }: { percent?: number; calm: boolean }) => {
  const used = Math.min(100, Math.max(0, percent));
  let tone = 'text-success';
  if (!calm && used >= 100) tone = 'text-error';
  else if (!calm && used >= 80) tone = 'text-warning-high-emphasis';

  return (
    <svg viewBox="0 0 20 20" className="h-5 w-5 -rotate-90" aria-hidden="true">
      <circle
        cx="10"
        cy="10"
        r={RADIUS}
        fill="none"
        strokeWidth="2.5"
        stroke="currentColor"
        className="text-border"
      />
      <circle
        cx="10"
        cy="10"
        r={RADIUS}
        fill="none"
        strokeWidth="2.5"
        strokeLinecap="round"
        stroke="currentColor"
        className={tone}
        strokeDasharray={CIRCUMFERENCE}
        strokeDashoffset={CIRCUMFERENCE * (1 - used / 100)}
      />
    </svg>
  );
};

const LimitRow = ({
  label,
  detail,
  value,
  percent,
  calm,
}: {
  label: string;
  detail?: ReactNode;
  value: string;
  percent?: number;
  calm?: boolean;
}) => (
  <div className="space-y-1.5">
    <div className="flex items-baseline justify-between gap-3 text-xs">
      <span className="min-w-0 truncate font-medium text-foreground">{label}</span>
      <span className="flex shrink-0 items-baseline gap-2 text-muted-foreground">
        {detail && <span>{detail}</span>}
        <span className={cn(percent !== undefined && 'font-mono')}>{value}</span>
      </span>
    </div>
    {percent !== undefined && <UsageBar percent={percent} calm={calm} />}
  </div>
);

/**
 * A small ring after the model picker for today's limit: hover for the number and when it resets,
 * click for every limit that holds. Nothing is shown without a plan or top-up credits. `isBusy` is
 * true while a reply is on its way; the limits are read again when it ends.
 */
export const UsageLimitCircle = ({ isBusy = false }: { isBusy?: boolean }) => {
  const { t } = useTranslation();
  useRefreshUsageAfterReply(isBusy);
  const indicator = useUsageLimitIndicator();
  const topUpCovers = useTopUpCoversUsage();
  const [isOpen, setIsOpen] = useState(false);

  if (!indicator) return null;

  const { summary, planName, windows, plan, topUpCredits } = indicator;
  const resets = (iso?: string) => {
    const when = iso ? formatResetTime(iso, t) : '';
    return when ? t('WINDOW_RESETS', { when, defaultValue: 'Resets {{when}}' }) : '';
  };
  const creditsLeft = (credits: number) =>
    t('CREDITS_LEFT', {
      credits: formatNumber(Math.floor(credits)),
      defaultValue: '{{credits}} credits left',
    });
  const summaryValue =
    summary.percent === undefined ? creditsLeft(topUpCredits ?? 0) : formatPercent(summary.percent, 2);
  const summaryResets = resets(summary.resetsAt);

  return (
    <TooltipProvider delayDuration={300}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={t('USAGE_LIMITS', 'Usage limits')}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <UsageRing
                  percent={summary.percent ?? 100}
                  calm={topUpCovers || summary.percent === undefined}
                />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          {!isOpen && (
            <TooltipContent
              side="bottom"
              align="end"
              className={cn(CARD_CLASS, 'space-y-0.5 px-3 py-2 text-foreground')}
            >
              <p className="text-xs font-medium text-foreground">
                {summary.label}: {summaryValue}
              </p>
              {summaryResets && <p className="text-xs text-muted-foreground">{summaryResets}</p>}
            </TooltipContent>
          )}
        </Tooltip>
        <PopoverContent
          side="bottom"
          align="end"
          className="w-80 max-w-[calc(100vw-2rem)] space-y-3"
        >
          <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
            <span className="min-w-0 truncate">
              {t('KLAX_USAGE_LIMITS', 'Klax usage limits')}
              {planName && ` · ${planName}`}
            </span>
            <Link
              to="/usage"
              onClick={() => setIsOpen(false)}
              className="inline-flex shrink-0 items-center gap-1 font-medium text-foreground"
            >
              {t('SEE_ALL', 'See all')}
              <ArrowRight aria-hidden="true" className="h-3 w-3" />
            </Link>
          </div>
          {plan && (
            <LimitRow
              label={t('MONTHLY_AI_LIMITS', 'Monthly · AI limits')}
              detail={`${formatShortDate(plan.start)} - ${formatShortDate(plan.end)}`}
              value={formatPercent(plan.percent, 2)}
              percent={plan.percent}
              calm={topUpCovers}
            />
          )}
          {windows.map((window) => (
            <LimitRow
              key={window.key}
              label={formatWindowName(window.name, t)}
              detail={resets(window.resets_at)}
              value={formatPercent(window.percent, 2)}
              percent={window.percent}
              calm={topUpCovers}
            />
          ))}
          {topUpCredits !== undefined && (
            <LimitRow
              label={t('TOP_UP_BALANCE', 'Top-up balance')}
              value={creditsLeft(topUpCredits)}
            />
          )}
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  );
};
